import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { FeatureFlag, Profile, Subtemplate, Template } from "PFTypes";
import { useCallback, useMemo } from "react";

import { useVisiblePermittedProperties } from "./use_visible_permitted_properties";

export const useGroupedProperties = (
  template: Template,
  subtemplate: Subtemplate | null,
  activityCreator: Profile | null
) => {
  const { getVisiblePermittedProperties } = useVisiblePermittedProperties();
  const isSegmentEnabled = useIsFeatureEnabled()(FeatureFlag.Segments);

  const getFilteredProperties = useCallback(
    (select: "match" | "segment" | "attribute") => {
      const visiblePermittedProperties = getVisiblePermittedProperties({
        subtemplate: select !== "segment" ? subtemplate : undefined,
        template,
        activityCreator
      });

      return visiblePermittedProperties.filter((property) => {
        const isSegmentProperty = isSegmentEnabled && !!property.segment;
        const isOtherProperty = ["availability", "booking_category_id"].includes(property.name);
        if (
          (select === "match" && !property.match) ||
          (select === "segment" && !isSegmentProperty) ||
          (select === "attribute" && (property.match || isSegmentProperty)) ||
          isOtherProperty
        ) {
          return false;
        }

        return true;
      });
    },
    [activityCreator, getVisiblePermittedProperties, isSegmentEnabled, subtemplate, template]
  );

  return useMemo(
    () => ({
      matchAttributesProperties: getFilteredProperties("match"),
      segmentProperties: getFilteredProperties("segment"),
      attributesProperties: getFilteredProperties("attribute")
    }),
    [getFilteredProperties]
  );
};
